import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function AdminRefunds() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ booking_id: "", amount: 0, note: "", kind: "stripe" });
  const load = async () => { const { data } = await api.get("/admin/refunds"); setRows(data); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      await api.post("/admin/refunds", { ...form, amount: Number(form.amount) });
      toast.success(form.kind === "stripe" ? "Stripe refund issued" : "Manual refund logged");
      setOpen(false); setForm({ booking_id: "", amount: 0, note: "", kind: "stripe" }); load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Refunds</h1><p className="text-slate-500 mt-1">Record refunds against bookings — Stripe auto-refund or manual out-of-band ledger entry.</p></div>
        <Button data-testid="rf-new" onClick={() => setOpen(true)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">Record refund</Button>
      </div>

      <Card className="mt-5 overflow-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Booking</TableHead><TableHead>User</TableHead><TableHead>Amount</TableHead><TableHead>Kind</TableHead><TableHead>Stripe refund</TableHead><TableHead>Note</TableHead><TableHead>Processed by</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} data-testid={`rf-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.booking_id.slice(0,8)}</TableCell>
                <TableCell>{r.user_email}</TableCell>
                <TableCell className="font-semibold">${Number(r.amount).toFixed(2)} {r.currency}</TableCell>
                <TableCell>
                  {r.kind === "stripe" || r.stripe_refund_id
                    ? <span className="text-[10px] uppercase font-semibold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">Stripe</span>
                    : <span className="text-[10px] uppercase font-semibold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">Manual</span>}
                </TableCell>
                <TableCell className="font-mono text-[10px] text-slate-500">{r.stripe_refund_id ? r.stripe_refund_id.slice(0,18) + "…" : "—"}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-xs truncate">{r.note}</TableCell>
                <TableCell className="text-xs">{r.processed_by}</TableCell>
                <TableCell className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-10">No refunds yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-xl text-[var(--brand-accent)]">Record refund</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Booking ID</Label><Input data-testid="rf-bid" value={form.booking_id} onChange={e=>setForm({...form,booking_id:e.target.value})}/></div>
            <div><Label>Amount</Label><Input data-testid="rf-amt" type="number" step="0.01" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})}/></div>
            <div>
              <Label className="block mb-2">Refund kind</Label>
              <div className="grid grid-cols-2 gap-2" role="radiogroup" data-testid="rf-kind">
                <button type="button" onClick={() => setForm({ ...form, kind: "stripe" })}
                        data-testid="rf-kind-stripe"
                        className={"border rounded-xl p-3 text-left " + (form.kind === "stripe" ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-slate-300")}>
                  <div className="font-semibold text-sm text-indigo-800">Stripe auto-refund</div>
                  <div className="text-[11px] text-slate-500 mt-1">Calls Stripe now — customer&apos;s card is credited within seconds.</div>
                </button>
                <button type="button" onClick={() => setForm({ ...form, kind: "manual" })}
                        data-testid="rf-kind-manual"
                        className={"border rounded-xl p-3 text-left " + (form.kind === "manual" ? "border-slate-500 bg-slate-50" : "border-slate-200 hover:border-slate-300")}>
                  <div className="font-semibold text-sm text-slate-700">Manual ledger entry</div>
                  <div className="text-[11px] text-slate-500 mt-1">Just log it — for bank wires, cash refunds or already-completed out-of-band refunds.</div>
                </button>
              </div>
            </div>
            <div><Label>Note</Label><Textarea data-testid="rf-note" rows={3} value={form.note} onChange={e=>setForm({...form,note:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="rf-create" onClick={create} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">{form.kind === "stripe" ? "Refund via Stripe" : "Save manual entry"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
